/**
 * Honest report — two dependent selects and the report they reveal.
 *
 * The product list is server-rendered as JSON, so narrowing flavours to the
 * chosen age is local: no fetch, no flash of the wrong options.
 *
 * The reference gates its button on all three of its steps. With the batch
 * step gone, "both steps answered" is the gate — except where the chosen age
 * has no flavoured products at all, in which case the flavour select would
 * stay permanently empty and the button could never enable. There, step 2
 * reports itself as not applicable and the age alone is enough.
 */
class LJHonestReport extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('[data-hrep-products]');
    this.products = [];
    if (raw) {
      try {
        this.products = JSON.parse(raw.textContent) || [];
      } catch (error) {
        // A malformed blob should leave a dead form, not a broken page.
        this.products = [];
      }
    }

    this.ageSelect = this.querySelector('[data-hrep-age]');
    this.flavourSelect = this.querySelector('[data-hrep-flavour]');
    this.submit = this.querySelector('[data-hrep-submit]');
    this.report = this.querySelector('[data-hrep-report]');
    if (!this.ageSelect || !this.flavourSelect || !this.submit) return;

    this.agePlaceholder = this.ageSelect.options[0] ? this.ageSelect.options[0].textContent : '';
    this.flavourPlaceholder = this.flavourSelect.options[0]
      ? this.flavourSelect.options[0].textContent
      : '';

    this.fillAges();

    this.ageSelect.addEventListener('change', () => {
      this.fillFlavours();
      this.syncSubmit();
      this.hideReport();
    });
    this.flavourSelect.addEventListener('change', () => {
      this.syncSubmit();
      this.hideReport();
    });
    this.submit.addEventListener('click', () => this.showReport());
  }

  /** Distinct values in the order the store returns them, blanks dropped. */
  static distinct(values) {
    const out = [];
    values.forEach((v) => {
      if (v && out.indexOf(v) === -1) out.push(v);
    });
    return out;
  }

  static option(value, label) {
    const el = document.createElement('option');
    el.value = value;
    el.textContent = label;
    return el;
  }

  fillAges() {
    const ages = LJHonestReport.distinct(this.products.map((p) => p.age));
    ages.forEach((age) => this.ageSelect.append(LJHonestReport.option(age, age)));
  }

  fillFlavours() {
    const age = this.ageSelect.value;
    const flavours = LJHonestReport.distinct(
      this.products.filter((p) => p.age === age).map((p) => p.flavour)
    );

    this.flavourSelect.textContent = '';
    this.flavourSelect.append(LJHonestReport.option('', this.flavourPlaceholder));
    flavours.forEach((f) => this.flavourSelect.append(LJHonestReport.option(f, f)));

    this.flavourNotApplicable = age !== '' && flavours.length === 0;
    this.flavourSelect.disabled = age === '' || this.flavourNotApplicable;
    if (this.flavourNotApplicable) {
      this.flavourSelect.options[0].textContent = '—';
    }
  }

  syncSubmit() {
    const hasAge = this.ageSelect.value !== '';
    const hasFlavour = this.flavourSelect.value !== '';
    this.submit.disabled = !(hasAge && (hasFlavour || this.flavourNotApplicable));
  }

  match() {
    const age = this.ageSelect.value;
    const flavour = this.flavourSelect.value;
    return this.products.find(
      (p) => p.age === age && (flavour === '' ? true : p.flavour === flavour)
    );
  }

  hideReport() {
    if (this.report) this.report.hidden = true;
  }

  showReport() {
    if (!this.report) return;
    const product = this.match();

    const media = this.report.querySelector('[data-hrep-image]');
    const name = this.report.querySelector('[data-hrep-name]');
    const list = this.report.querySelector('[data-hrep-authorities]');

    if (!product) {
      if (media) media.textContent = '';
      if (name) name.textContent = this.dataset.emptyText || '';
      if (list) list.textContent = '';
      this.report.hidden = false;
      this.report.focus();
      return;
    }

    if (media) {
      media.textContent = '';
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = product.title;
        img.loading = 'lazy';
        media.append(img);
      }
    }

    if (name) name.textContent = product.title;

    if (list) {
      list.textContent = '';
      (this.dataset.authorities || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .forEach((line) => {
          const li = document.createElement('li');
          li.textContent = line;
          list.append(li);
        });
    }

    this.report.hidden = false;
    this.report.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.report.focus({ preventScroll: true });
  }
}

customElements.define('lj-honest-report', LJHonestReport);
